<template>
  <div class="pending-approvals-table">
    <app-loading :loading="loading" />
    <template v-if="!loading">
      <b-table
        v-if="nominations.length > 0"
        :items="enrichedNominations"
        :fields="tableFields"
        hover
        responsive
        borderless
        show-empty
        class="pending-approvals-table__table"
      >
        <template #cell(full_name)="{ item }">
          <span class="pending-approvals-table__name">{{ item.full_name }}</span>
        </template>

        <template #cell(division)="{ item }">
          <span v-if="item.employeeDivisionNames.length > 0">{{ item.employeeDivisionNames.join(', ') }}</span>
          <span v-else class="text-muted">-</span>
        </template>

        <template #cell(email)="{ item }">
          <span class="pending-approvals-table__email">{{ item.email }}</span>
        </template>

        <template #cell(program_name)="{ item }">
          <span>{{ item.program_name }}</span>
        </template>

        <template #cell(nominated_by)="{ item }">
          <template v-if="item.nominated_by && item.nominated_by.length > 0">
            <template v-if="item.nominated_by.length === 1">
              <b-badge pill variant="light" class="pending-approvals-table__nominator-badge">
                {{ item.nominated_by[0].full_name }}
              </b-badge>
            </template>
            <template v-else>
              <span
                :id="`nominators-popover-${item.nominated_by[0].recognition_nomination_id}`"
                class="pending-approvals-table__nominators-trigger"
              >
                <b-badge pill variant="light" class="pending-approvals-table__nominator-badge">
                  {{ item.nominated_by.length }} nominators
                </b-badge>
              </span>
              <b-popover
                :target="`nominators-popover-${item.nominated_by[0].recognition_nomination_id}`"
                triggers="hover focus"
                placement="bottom"
              >
                <div class="pending-approvals-table__popover-container">
                  <div v-for="nominator in item.nominated_by" :key="nominator.recognition_nomination_id">
                    {{ nominator.full_name }}
                  </div>
                </div>
              </b-popover>
            </template>
          </template>
          <template v-else>
            <span class="text-muted">-</span>
          </template>
        </template>

        <template #cell(actions)="{ item }">
          <b-button size="xs" variant="outline-primary" @click="emit('review', item)">Review</b-button>
        </template>
      </b-table>

      <div v-else class="pending-approvals-table__empty">
        <p class="text-muted">No pending approvals.</p>
      </div>
    </template>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import AppLoading from '@/components/AppLoading.vue';
import useDivisionNames from '@/pages/recognition/composables/useDivisionNames';

const props = defineProps({
  nominations: {
    type: Array,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
  showProgramColumn: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(['review']);

const { divisionNamesFor } = useDivisionNames();

const enrichedNominations = computed(() =>
  props.nominations.map(nomination => ({
    ...nomination,
    employeeDivisionNames: divisionNamesFor(nomination.employee_division_ids),
  })),
);

const BASE_FIELDS = [
  {
    key: 'full_name',
    label: 'Name',
  },
  {
    key: 'division',
    label: 'Division',
  },
  {
    key: 'email',
    label: 'Email',
  },
];

const PROGRAM_FIELD = {
  key: 'program_name',
  label: 'Award',
};

const NOMINATED_BY_FIELD = {
  key: 'nominated_by',
  label: 'Nominators',
};

const ACTIONS_FIELD = {
  key: 'actions',
  label: '',
  class: 'text-right',
};

const tableFields = computed(() => {
  const fields = [...BASE_FIELDS];
  if (props.showProgramColumn) {
    fields.push(PROGRAM_FIELD);
  }
  fields.push(NOMINATED_BY_FIELD);
  fields.push(ACTIONS_FIELD);
  return fields;
});
</script>

<style scoped lang="scss">
.pending-approvals-table {
  &__table {
    :deep(thead th) {
      font-weight: 500;
      font-size: 0.875rem;
      color: var(--palette-color-default-lighten-40);
      border-bottom: 1px solid var(--palette-color-default-lighten-90);
      padding: 0.75rem 1rem;
    }

    :deep(tbody td) {
      padding: 1rem;
      vertical-align: middle;
      border-bottom: 1px solid var(--palette-color-default-lighten-90);
    }

    :deep(tbody tr:hover) {
      background-color: var(--palette-color-default-lighten-95);
    }
  }

  &__name {
    font-weight: 500;
  }

  &__email {
    color: var(--palette-color-default);
  }

  &__nominator-badge {
    background-color: var(--palette-color-default-lighten-90);
    color: var(--palette-color-default);
    font-weight: 400;
    padding: 0.375rem 0.75rem;
  }

  &__nominators-trigger {
    cursor: pointer;
  }

  &__popover-container {
    margin-top: 1rem;
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  &__empty {
    padding: 3rem;
    text-align: center;
  }
}
</style>
